<template>
  <div>
    <HrPageLayout
      title="My Documents"
      subtitle="View and manage your personal documents"
      icon="📁"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'Documents' }
      ]"
    >
      <div class="employee-documents-content">
        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading documents...</p>
        </div>

        <template v-else-if="documentsData">
          <!-- Stats -->
          <div class="hr-metrics-grid hr-mb-4">
            <div class="hr-metric-card gradient-1">
              <div class="hr-metric-icon"><i class="bx bxs-file-doc"></i></div>
              <div class="hr-metric-content">
              <p class="hr-metric-label">Total Documents</p>
              <h2 class="hr-metric-value">{{ documentsData.statistics.totalDocuments }}</h2>
            </div>
            </div>
            <div class="hr-metric-card gradient-2">
              <div class="hr-metric-icon"><i class="bx bx-folder"></i></div>
              <div class="hr-metric-content">
              <p class="hr-metric-label">Categories</p>
              <h2 class="hr-metric-value">{{ documentsData.statistics.categoryCount }}</h2>
            </div>
            </div>
            <div class="hr-metric-card gradient-3">
              <div class="hr-metric-icon"><i class="bx bx-hdd"></i></div>
              <div class="hr-metric-content">
              <p class="hr-metric-label">Total Size</p>
              <h2 class="hr-metric-value">{{ documentsData.statistics.totalSizeMB }} MB</h2>
            </div>
            </div>
            <div class="hr-metric-card gradient-4">
              <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
              <p class="hr-metric-label">Latest Upload</p>
              <h2 class="hr-metric-value hr-metric-subtext">{{ getLatestUploadDate() }}</h2>
            </div>
          </div>

          <!-- Filters -->
          <div class="hr-chart-card hr-mb-4">
            <div class="hr-chart-body hr-py-3">
              <div class="doc-view-filters">
                <div class="hr-search-wrap">
                  <input v-model="filters.search" @input="debounceSearch" class="hr-input" type="text"
                         placeholder="Search by name or description...">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
                <div class="hr-filter-group">
                  <label class="hr-form-label">Category</label>
                  <select v-model="filters.category" @change="loadDocuments" class="hr-select">
                    <option value="">All Categories</option>
                    <option value="Contract">Employment Contracts</option>
                    <option value="ID">Identification Documents</option>
                    <option value="Certificate">Certificates</option>
                    <option value="Letter">Letters</option>
                    <option value="Policy">Policies</option>
                    <option value="Payslip">Payslips</option>
                    <option value="Tax">Tax Documents</option>
                    <option value="Leave">Leave Documents</option>
                    <option value="Performance">Performance Reviews</option>
                    <option value="Training">Training Materials</option>
                    <option value="Medical">Medical Documents</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div class="hr-filter-group">
                  <label class="hr-form-label">View</label>
                  <select v-model="viewMode" class="hr-select">
                    <option value="grid">Grid View</option>
                    <option value="list">List View</option>
                  </select>
                </div>
                <button class="hr-btn hr-btn-secondary" @click="resetFilters">
                  <i class="bx bx-refresh"></i> Reset
                </button>
                <button class="hr-btn hr-btn-success" @click="showRequestModal">
                  <i class="bx bx-plus"></i> Request Document
                </button>
              </div>
            </div>
          </div>

          <!-- Category Stats -->
          <div v-if="documentsData.categoryStats?.length" class="hr-chart-card hr-mb-4">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt"></i> Documents by Category</h5>
            </div>
            <div class="hr-chart-body">
              <div class="doc-category-grid">
                <div v-for="(stat, index) in documentsData.categoryStats" :key="index" class="doc-category-item">
                  <div class="hr-flex-between hr-mb-2">
                    <i :class="getCategoryIcon(stat.documentType)" class="bx doc-cat-icon"></i>
                    <h6 class="hr-mb-0">{{ stat.documentType }}</h6>
                  </div>
                  <h4 class="hr-mb-0">{{ stat.count }}</h4>
                  <p class="hr-text-muted hr-mb-0 hr-small">
                    {{ ((stat.count / documentsData.statistics.totalDocuments) * 100).toFixed(0) }}% of total
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Documents Grid/List -->
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-file"></i> My Documents</h5>
            </div>
            <div class="hr-chart-body">
              <!-- Grid View -->
              <div v-if="viewMode === 'grid'" class="doc-grid">
                <div v-if="documentsData.documents?.length" class="doc-grid-inner">
                  <div v-for="doc in documentsData.documents" :key="doc.id" class="doc-card">
                    <div class="doc-card-icon" :style="{ color: getFileColor(doc.fileType) }">
                      <i :class="getFileIcon(doc.fileType)"></i>
                    </div>
                    <h6 class="doc-card-title" :title="doc.name">{{ doc.name }}</h6>
                    <span class="hr-badge hr-badge-primary hr-mb-2">{{ doc.documentType }}</span>
                    <span v-if="isDocumentApproved(doc)" class="hr-badge hr-badge-success hr-mb-2">
                      <i class="bx bx-check"></i> Approved
                    </span>
                    <span v-else class="hr-badge hr-badge-warning hr-mb-2">
                      <i class="bx bx-time-five"></i> Pending
                    </span>
                    <p class="hr-text-muted hr-small hr-mb-2">
                      {{ formatFileSize(doc.fileSize) }} • {{ doc.upload_date_formatted }}
                    </p>
                    <p v-if="doc.documentNumber" class="hr-text-muted hr-small hr-mb-3 doc-desc">{{ doc.documentNumber }}</p>
                    <div class="doc-card-actions">
                      <button class="hr-btn hr-btn-sm hr-btn-primary" @click="viewDocument(doc)">
                        <i class="bx bx-show"></i> View
                      </button>
                      <button class="hr-btn hr-btn-sm hr-btn-success" @click="downloadDocument(doc)">
                        <i class="bx bx-download"></i> Download
                      </button>
                      <button class="hr-btn hr-btn-sm hr-btn-danger" @click="confirmDelete(doc)">
                        <i class="bx bx-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="hr-text-center hr-py-5 hr-text-muted">
                  <i class="bx bx-file" style="font-size: 48px;"></i>
                  <h5 class="hr-mt-2">No documents found</h5>
                  <p>Try adjusting your filters or request a document from HR</p>
                </div>
              </div>

              <!-- List View -->
              <div v-if="viewMode === 'list'" class="hr-table-wrapper">
                <table v-if="documentsData.documents?.length" class="hr-table hr-table-hover">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>Uploaded</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="doc in documentsData.documents" :key="doc.id">
                      <td>
                        <div class="doc-list-cell">
                          <i :class="getFileIcon(doc.fileType)" class="doc-list-icon" :style="{ color: getFileColor(doc.fileType) }"></i>
                          <div>
                            <h6 class="hr-mb-0">{{ doc.name }}</h6>
                            <p v-if="doc.documentNumber" class="hr-text-muted hr-mb-0 hr-small">{{ doc.documentNumber }}</p>
                          </div>
                        </div>
                      </td>
                      <td><span class="hr-badge hr-badge-primary">{{ doc.documentType }}</span></td>
                      <td>
                        <span v-if="isDocumentApproved(doc)" class="hr-badge hr-badge-success">
                          <i class="bx bx-check"></i> Approved
                        </span>
                        <span v-else class="hr-badge hr-badge-warning">
                          <i class="bx bx-time-five"></i> Pending
                        </span>
                      </td>
                      <td class="hr-text-uppercase hr-small">{{ getFileExtension(doc.fileType) }}</td>
                      <td>{{ formatFileSize(doc.fileSize) }}</td>
                      <td class="hr-text-muted">{{ doc.upload_date_formatted }}</td>
                      <td>
                        <div class="hr-btn-group">
                          <button class="hr-btn hr-btn-sm hr-btn-primary" @click="viewDocument(doc)" title="View">
                            <i class="bx bx-show"></i>
                          </button>
                          <button class="hr-btn hr-btn-sm hr-btn-success" @click="downloadDocument(doc)" title="Download">
                            <i class="bx bx-download"></i>
                          </button>
                          <button class="hr-btn hr-btn-sm hr-btn-danger" @click="confirmDelete(doc)" title="Delete">
                            <i class="bx bx-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="hr-text-center hr-py-5 hr-text-muted">
                  <i class="bx bx-file" style="font-size: 48px;"></i>
                  <h5 class="hr-mt-2">No documents found</h5>
                  <p>Try adjusting your filters or request a document from HR</p>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="documentsData.documents?.length && documentsData.pagination?.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
                <div class="hr-pagination-info">
                  Showing {{ ((documentsData.pagination.currentPage - 1) * documentsData.pagination.limit) + 1 }}
                  to {{ Math.min(documentsData.pagination.currentPage * documentsData.pagination.limit, documentsData.pagination.totalRecords) }}
                  of {{ documentsData.pagination.totalRecords }} documents
                </div>
                <nav class="hr-pagination">
                  <button type="button" class="hr-page-btn" :disabled="documentsData.pagination.currentPage <= 1"
                          @click="changePage(documentsData.pagination.currentPage - 1)">
                    <i class="bx bx-chevron-left"></i>
                  </button>
                  <template v-for="page in getPageNumbers()" :key="String(page)">
                    <button v-if="page !== '...'" type="button" class="hr-page-btn"
                            :class="{ 'hr-page-active': page === documentsData.pagination.currentPage }"
                            @click="changePage(page)">
                      {{ page }}
                    </button>
                    <span v-else class="hr-page-btn hr-page-ellipsis">{{ page }}</span>
                  </template>
                  <button type="button" class="hr-page-btn"
                          :disabled="documentsData.pagination.currentPage >= documentsData.pagination.totalPages"
                          @click="changePage(documentsData.pagination.currentPage + 1)">
                    <i class="bx bx-chevron-right"></i>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </template>
      </div>
    </HrPageLayout>

    <!-- Request Document Modal -->
    <ModalWrapper ref="requestDocumentModal" title="Request Document Upload" size="medium">
      <template #body>
        <form @submit.prevent="submitDocumentRequest" class="hr-form-grid">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Document Type <span class="hr-text-danger">*</span></label>
            <select v-model="documentRequest.type" class="hr-select" required>
              <option value="">Select document type</option>
              <option value="Contract">Employment Contract</option>
              <option value="ID">ID Document</option>
              <option value="Certificate">Certificate</option>
              <option value="Letter">Letter</option>
              <option value="Tax">Tax Document</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Document Description <span class="hr-text-danger">*</span></label>
            <input v-model="documentRequest.description" type="text" class="hr-input"
                   placeholder="E.g., Updated Employment Contract" required>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Reason for Request</label>
            <textarea v-model="documentRequest.reason" class="hr-textarea" rows="3"
                      placeholder="Why do you need this document?"></textarea>
          </div>
          <div class="hr-alert hr-alert-info hr-mt-3">
            <i class="bx bx-info-circle"></i>
            <small>Your request will be sent to HR for processing. You'll be notified once the document is uploaded.</small>
          </div>
        </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.requestDocumentModal?.close()">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" @click="submitDocumentRequest" :disabled="submitting">
          <span v-if="submitting" class="hr-spinner small"></span>
          <i v-else class="bx bx-send"></i> Submit Request
        </button>
      </template>
    </ModalWrapper>

    <!-- Delete Confirmation Modal -->
    <ModalWrapper ref="deleteConfirmModal" title="Confirm Deletion" size="medium">
      <template #body>
        <div v-if="documentToDelete" class="hr-text-center">
          <i class="bx bx-error-circle" style="font-size: 48px; color: #ef4444;"></i>
          <p class="hr-mt-3">Are you sure you want to delete this document?</p>
          <div class="hr-alert hr-alert-warning hr-text-left">
            <h6 class="hr-mb-2">
              <i :class="getFileIcon(documentToDelete.fileType)" class="bx hr-mr-2"></i>
              {{ documentToDelete.name }}
            </h6>
            <p class="hr-mb-0 hr-small">
              <strong>Type:</strong> {{ documentToDelete.documentType }}<br>
              <strong>Size:</strong> {{ formatFileSize(documentToDelete.fileSize) }}
            </p>
          </div>
          <div class="hr-alert hr-alert-danger hr-text-left">
            <i class="bx bx-error"></i>
            <strong>Warning:</strong> This action cannot be undone. The document will be permanently removed.
          </div>
          <div class="hr-filter-group hr-mt-3">
            <label class="hr-form-label"><strong>Type <code class="hr-text-danger">DELETE</code> to confirm:</strong></label>
            <input v-model="deleteConfirmText" type="text" class="hr-input" placeholder="Type DELETE here"
                   @input="deleteConfirmText = deleteConfirmText.toUpperCase()"
                   :class="{ 'hr-input-valid': isDeleteConfirmed, 'hr-input-invalid': deleteConfirmText && !isDeleteConfirmed }">
            <div v-if="deleteConfirmText && !isDeleteConfirmed" class="hr-text-danger hr-small hr-mt-1">
              Please type exactly "DELETE" to confirm
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="closeDeleteModal">Cancel</button>
        <button type="button" class="hr-btn hr-btn-danger" @click="executeDelete"
                :disabled="deleting || !isDeleteConfirmed">
          <span v-if="deleting" class="hr-spinner small"></span>
          <i v-else class="bx bx-trash"></i>
          {{ deleting ? 'Deleting...' : 'Delete Document' }}
        </button>
      </template>
    </ModalWrapper>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, uploads } from "@/api";

export default {
  name: "EmployeeDocumentsView",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      submitting: false,
      deleting: false,
      documentsData: null,
      filters: { category: '', search: '', page: 1 },
      viewMode: 'grid',
      searchTimeout: null,
      documentToDelete: null,
      deleteConfirmText: '',
      documentRequest: { type: '', description: '', reason: '' }
    };
  },
  computed: {
    isDeleteConfirmed() {
      return this.deleteConfirmText === 'DELETE';
    }
  },
  mounted() {
    this.loadDocuments();
  },
  methods: {
    async loadDocuments() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeDocuments");
        data.append("page", this.filters.page);
        data.append("limit", 50);
        if (this.filters.category) data.append("category", this.filters.category);
        if (this.filters.search) data.append("search", this.filters.search);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.documentsData = res.data.data;
        } else {
          throw new Error(res?.data?.message || 'Failed to load documents');
        }
      } catch (error) {
        this.$toast?.error("Failed to load documents: " + error.message) || console.error(error);
      } finally {
        this.loading = false;
      }
    },
    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filters.page = 1;
        this.loadDocuments();
      }, 500);
    },
    resetFilters() {
      this.filters = { category: '', search: '', page: 1 };
      this.loadDocuments();
    },
    changePage(page) {
      if (page < 1 || page === '...' || page > this.documentsData?.pagination?.totalPages) return;
      this.filters.page = page;
      this.loadDocuments();
    },
    getPageNumbers() {
      const p = this.documentsData?.pagination;
      if (!p || p.totalPages <= 7) {
        return p ? Array.from({ length: p.totalPages }, (_, i) => i + 1) : [];
      }
      const cp = p.currentPage;
      if (cp <= 3) return [1, 2, 3, 4, '...', p.totalPages];
      if (cp >= p.totalPages - 2) return [1, '...', p.totalPages - 3, p.totalPages - 2, p.totalPages - 1, p.totalPages];
      return [1, '...', cp - 1, cp, cp + 1, '...', p.totalPages];
    },
    async downloadDocument(doc) {
      if (doc.filePath) {
        window.open(uploads + '' + doc.filePath, '_blank');
        this.$toast?.success("Document opened successfully");
        try {
          const data = new FormData();
          data.append("function", "downloadEmployeeDocument");
          data.append("document_id", doc.id);
          await execute(data, authUrl);
        } catch (e) { /* silent */ }
      } else {
        this.$toast?.error("Document file not available");
      }
    },
    viewDocument(doc) {
      this.downloadDocument(doc);
    },
    confirmDelete(doc) {
      this.documentToDelete = doc;
      this.deleteConfirmText = '';
      this.$refs.deleteConfirmModal?.open();
    },
    closeDeleteModal() {
      this.documentToDelete = null;
      this.deleteConfirmText = '';
      this.$refs.deleteConfirmModal?.close();
    },
    async executeDelete() {
      if (!this.documentToDelete || !this.isDeleteConfirmed) return;
      this.deleting = true;
      try {
        const data = new FormData();
        data.append("function", "deleteEmployeeDocument");
        data.append("document_id", this.documentToDelete.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success("Document deleted successfully");
          this.closeDeleteModal();
          this.loadDocuments();
        } else {
          throw new Error(res?.data?.message || 'Delete failed');
        }
      } catch (error) {
        this.$toast?.error("Delete failed: " + error.message);
      } finally {
        this.deleting = false;
      }
    },
    showRequestModal() {
      this.documentRequest = { type: '', description: '', reason: '' };
      this.$refs.requestDocumentModal?.open();
    },
    async submitDocumentRequest() {
      if (!this.documentRequest.type || !this.documentRequest.description) {
        this.$toast?.error("Please fill in all required fields");
        return;
      }
      this.submitting = true;
      try {
        const data = new FormData();
        data.append("function", "requestDocumentUpload");
        data.append("document_type", this.documentRequest.type);
        data.append("description", this.documentRequest.description);
        data.append("reason", this.documentRequest.reason);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success("Document request submitted successfully");
          this.$refs.requestDocumentModal?.close();
          this.documentRequest = { type: '', description: '', reason: '' };
        } else {
          throw new Error(res?.data?.message || 'Request failed');
        }
      } catch (error) {
        this.$toast?.error("Request failed: " + error.message);
      } finally {
        this.submitting = false;
      }
    },
    getFileIcon(fileType) {
      const t = fileType?.toLowerCase() || '';
      if (t.includes('pdf')) return 'bx bxs-file-pdf';
      if (t.includes('doc')) return 'bx bxs-file-doc';
      if (t.includes('xls') || t.includes('sheet')) return 'bx bxs-file';
      if (t.includes('ppt')) return 'bx bxs-file';
      if (t.includes('image') || t.includes('jpg') || t.includes('png')) return 'bx bxs-file-image';
      if (t.includes('zip') || t.includes('rar')) return 'bx bxs-file-archive';
      return 'bx bx-file';
    },
    isDocumentApproved(doc) {
      const v = doc?.isVerified;
      if (v === true || v === 1) return true;
      const s = String(v ?? '').trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'approved' || s === 'verified';
    },
    getFileExtension(fileType) {
      const t = fileType?.toLowerCase() || '';
      if (t.includes('pdf')) return 'PDF';
      if (t.includes('msword')) return 'DOC';
      if (t.includes('wordprocessingml')) return 'DOCX';
      if (t.includes('excel') || t.includes('spreadsheet')) return 'XLSX';
      if (t.includes('jpeg')) return 'JPG';
      if (t.includes('png')) return 'PNG';
      return 'FILE';
    },
    getFileColor(fileType) {
      const t = fileType?.toLowerCase() || '';
      if (t.includes('pdf')) return '#d32f2f';
      if (t.includes('doc')) return '#2196f3';
      if (t.includes('xls')) return '#4caf50';
      if (t.includes('ppt')) return '#ff9800';
      if (t.includes('image')) return '#9c27b0';
      if (t.includes('zip')) return '#795548';
      return '#607d8b';
    },
    getCategoryIcon(category) {
      const icons = {
        Contract: 'bx bx-file',
        ID: 'bx bx-id-card',
        Certificate: 'bx bx-award',
        Letter: 'bx bx-envelope',
        Policy: 'bx bx-check-shield',
        Payslip: 'bx bx-dollar',
        Tax: 'bx bx-calculator',
        Leave: 'bx bx-calendar',
        Performance: 'bx bx-trending-up',
        Training: 'bx bx-book',
        Medical: 'bx bx-plus-medical',
        Other: 'bx bx-file'
      };
      return icons[category] || 'bx bx-file';
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
    },
    getLatestUploadDate() {
      if (!this.documentsData?.recentDocuments?.length) return 'N/A';
      return this.documentsData.recentDocuments[0].upload_date_formatted;
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.doc-view-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.doc-category-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; }
.doc-category-item { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
.doc-cat-icon { font-size: 24px; color: #5b73e8; }
.doc-grid { display: block; }
.doc-grid-inner { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 20px; }
.doc-card { border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px; text-align: center; transition: all 0.3s; }
.doc-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); border-color: #5b73e8; }
.doc-card-icon { font-size: 48px; margin-bottom: 12px; }
.doc-card-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 8px; }
.doc-card-actions { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 12px; }
.doc-desc { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.doc-list-cell { display: flex; align-items: center; gap: 12px; }
.doc-list-icon { font-size: 24px; }
.hr-alert { padding: 12px 16px; border-radius: 8px; margin-bottom: 12px; }
.hr-alert-info { background: #eff6ff; border-left: 4px solid #3b82f6; }
.hr-alert-warning { background: #fffbeb; border-left: 4px solid #f59e0b; }
.hr-alert-danger { background: #fef2f2; border-left: 4px solid #ef4444; }
.hr-text-left { text-align: left; }
.hr-text-uppercase { text-transform: uppercase; }
.hr-mr-2 { margin-right: 8px; }
.hr-page-ellipsis { cursor: default; opacity: 0.7; }
</style>
